import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Grid,
  Typography,
  Paper,
  Chip,
  Alert,
  CircularProgress,
} from '@mui/material';
import {
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { ROLE_LABELS } from '../types/user';
import apiClient from '../services/apiClient'; // Usiamo il nostro client robusto
import HomeAnnouncementBanner from '../components/HomeAnnouncementBanner';

interface HealthStatus {
  status: string;
  message: string;
  database?: {
    connected: boolean;
    message: string;
  };
}

const Home: React.FC = () => {
  const { user } = useAuth();
  const [healthStatus, setHealthStatus] = useState<HealthStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    const checkHealth = async () => {
      try {
        // La chiamata ora usa apiClient
        const response = await apiClient.get('/api/health');
        setHealthStatus(response.data);
      } catch (err) {
        setError('Impossibile verificare lo stato del sistema');
      } finally {
        setLoading(false);
      }
    };

    checkHealth();
  }, []);

  return (
    <Box>
      <HomeAnnouncementBanner />
      <Typography variant="h4" gutterBottom>
        Benvenuto, {user?.name}!
      </Typography>

      <Grid container spacing={3} sx={{ mt: 2 }}>
        {/* User Info */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Il tuo profilo
              </Typography>
              <Typography variant="h5" gutterBottom>
                {user?.email}
              </Typography>
              <Box sx={{ mt: 2 }}>
                <Chip
                  label={user ? ROLE_LABELS[user.role] : ''}
                  color={user?.role === 'ADMIN' ? 'primary' : 'default'}
                  size="small"
                />
                <Chip
                  label={user?.is_active ? 'Attivo' : 'Disattivato'}
                  color={user?.is_active ? 'success' : 'error'}
                  size="small"
                  sx={{ ml: 1 }}
                />
              </Box>
              <Typography variant="body2" sx={{ mt: 2 }}>
                Utente dal:{' '}
                {user?.created_at
                  ? new Date(user.created_at).toLocaleDateString('it-IT')
                  : 'n.d.'}
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        {/* System Health */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Stato del sistema
              </Typography>
              {loading ? (
                <Box display="flex" justifyContent="center" p={2}>
                  <CircularProgress size={24} />
                </Box>
              ) : error ? (
                <Alert severity="error">{error}</Alert>
              ) : (
                <>
                  <Box display="flex" alignItems="center" mb={2}>
                    <Typography variant="h6" sx={{ mr: 2 }}>
                      Servizio:
                    </Typography>
                    {healthStatus?.status === 'healthy' ? (
                      <Chip
                        icon={<CheckCircleIcon />}
                        label="Operativo"
                        color="success"
                        size="small"
                      />
                    ) : (
                      <Chip
                        icon={<ErrorIcon />}
                        label="Non operativo"
                        color="error"
                        size="small"
                      />
                    )}
                  </Box>

                  {healthStatus?.database ? (
                    <>
                      <Box display="flex" alignItems="center">
                        <Typography variant="h6" sx={{ mr: 2 }}>
                          Database:
                        </Typography>
                        {healthStatus.database.connected ? (
                          <Chip
                            icon={<CheckCircleIcon />}
                            label="Connesso"
                            color="success"
                            size="small"
                          />
                        ) : (
                          <Chip
                            icon={<ErrorIcon />}
                            label="Non connesso"
                            color="error"
                            size="small"
                          />
                        )}
                      </Box>
                      <Typography variant="body2" sx={{ mt: 1 }}>
                        {healthStatus.database.message}
                      </Typography>
                    </>
                  ) : (
                    <Typography variant="body2" color="textSecondary">
                      Nessuna informazione sul database.
                    </Typography>
                  )}
                </>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Help section */}
        <Grid item xs={12}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Guida rapida
            </Typography>
            <Typography variant="body1" paragraph>
              Benvenuto nella dashboard. Ecco cosa puoi fare:
            </Typography>
            <ul>
              <li>
                <Typography variant="body2">
                  Visualizzare e modificare il tuo profilo
                </Typography>
              </li>
              {user?.role === 'ADMIN' && (
                <>
                  <li>
                    <Typography variant="body2">
                      Gestire gli utenti e i loro permessi
                    </Typography>
                  </li>
                  <li>
                    <Typography variant="body2">
                      Controllare lo stato del sistema
                    </Typography>
                  </li>
                </>
              )}
              <li>
                <Typography variant="body2">
                  Accedere alle funzioni dal menu di navigazione
                </Typography>
              </li>
            </ul>
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
};

export default Home;